/**
 * Team page tree from seeded Page records.
 *
 * UUID `pid` is the stored parent pointer (parent page.pageId).
 * Slug (`home`, `retain/line`) is the address the UI and Cy use.
 */

export const HOME_SLUG = 'home';

export function recordPageId(record) {
  if (!record) return null;
  return record.page?.pageId || record.page?.id || record.pageId || null;
}

export function recordPid(record) {
  const pid = record?.pid;
  return pid ? String(pid) : null;
}

export function recordSlug(record) {
  const raw = String(record?.slug || '')
    .replace(/^\/+|\/+$/g, '')
    .trim();
  return raw || HOME_SLUG;
}

export function recordTeam(record) {
  return String(record?.team || '');
}

function asRecordArray(records) {
  return Array.isArray(records)
    ? records.filter(Boolean)
    : Object.values(records || {}).filter(Boolean);
}

export function catalogTeamForRecords(records, teamId = '', mainTeamId = '') {
  if (teamId || mainTeamId) return String(teamId || mainTeamId);
  const home = asRecordArray(records).find(
    (record) => recordSlug(record) === HOME_SLUG
  );
  return recordTeam(home);
}

/**
 * Shared slugs like home exist on every team. Prefer the working tree.
 */
export function findRecordBySlug(records, slug, teamId) {
  const key = recordSlug({ slug });
  const matches = asRecordArray(records).filter(
    (item) => recordSlug(item) === key
  );
  if (!matches.length) return null;
  const team = catalogTeamForRecords(records, teamId);
  if (team) {
    return matches.find((item) => recordTeam(item) === team) || null;
  }
  return matches[0];
}

/**
 * Nav, crumbs, and pageList must use the same working tree as openBySlug.
 * Without a team, keep the full list (admin unscoped catalog). A set
 * team with no rows stays empty — do not render another journey.
 */
export function recordsForTeam(records, teamId) {
  const list = asRecordArray(records);
  const team = String(teamId || '');
  if (!team) return list;
  return list.filter((item) => recordTeam(item) === team);
}

export function recordMeta(record) {
  return (record && (record.page?.meta || record.meta)) || {};
}

export function displayOf(record) {
  const display = recordMeta(record).display || record?.display;
  return display === 'hero' ? 'hero' : 'compact';
}

export function pageHref(slug) {
  const key = String(slug || HOME_SLUG).replace(/^\/+|\/+$/g, '');
  if (!key || key === HOME_SLUG) return '/';
  return `/pages/${key}`;
}

export function slugFromRoute(route) {
  if (!route) return HOME_SLUG;
  const { name, params = {} } = route;
  if (name === 'home') return HOME_SLUG;
  const section = params.section;
  const leaf = params.leaf;
  if (!section || section === HOME_SLUG) return HOME_SLUG;
  return [section, leaf].filter(Boolean).join('/');
}

export function asRecordList(data) {
  return Array.isArray(data) ? data : [];
}

export function asTemplateCatalog(data) {
  const raw = data?.templates;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw;
}

function shortSlug(slug) {
  if (!slug || slug === HOME_SLUG) return HOME_SLUG;
  const parts = String(slug).split('/').filter(Boolean);
  return parts[parts.length - 1] || HOME_SLUG;
}

function templatesFor(node, catalog) {
  if (!catalog) return [];
  const raw =
    catalog[node.slug] ||
    catalog[shortSlug(node.slug)] ||
    (node.depth === 1 ? catalog[node.section] : []) ||
    [];
  const taken = new Set(
    (node.children || []).map((child) => shortSlug(child.slug))
  );
  return raw
    .filter((tpl) => tpl && !taken.has(tpl.slug))
    .map((tpl) => ({
      ...tpl,
      section: node.slug,
      fullSlug: `${node.slug}/${tpl.slug}`,
    }));
}

export function templateKey(section, tpl) {
  if (tpl?.fullSlug) return tpl.fullSlug;
  const parent = section?.slug || section || '';
  const leaf = tpl?.slug || '';
  if (parent && leaf) return `${parent}/${leaf}`;
  return leaf || parent || '';
}

function compareSiblings(a, b) {
  const ia = a.meta?.index;
  const ib = b.meta?.index;
  if ((ia != null) !== (ib != null)) return ia != null ? -1 : 1;
  if (ia != null && Number(ia) !== Number(ib)) return Number(ia) - Number(ib);
  return a.slug.localeCompare(b.slug);
}

export function buildPageTree(
  records,
  { templatesBySection = {}, teamId, mainTeamId } = {}
) {
  const list = recordsForTeam(
    records,
    catalogTeamForRecords(records, teamId, mainTeamId)
  );
  const byPageId = {};
  const bySlug = {};
  const nodes = list.map((record) => {
    const slug = recordSlug(record);
    const meta = recordMeta(record);
    const node = {
      record,
      id: record.id,
      slug,
      pageId: recordPageId(record),
      pid: recordPid(record),
      name: record.name || record.title || slug,
      subtitle: meta.subtitle || '',
      icon: meta.icon || '',
      cover: meta.cover || null,
      display: displayOf(record),
      tags: record.tags || meta.tags || [],
      unlock: meta.unlock || null,
      progress: meta.progress || null,
      location: record.location,
      meta,
      children: [],
      templates: [],
      depth: 0,
      section: slug === HOME_SLUG ? null : slug.split('/')[0],
    };
    if (node.pageId) {
      byPageId[node.pageId] = node;
    }
    if (!bySlug[slug]) bySlug[slug] = node;
    return node;
  });

  const home =
    bySlug[HOME_SLUG] ||
    nodes.find((node) => !node.pid && node.location === 'home') ||
    null;

  nodes.forEach((node) => {
    if (!node.pid || node === home) return;
    const parent = byPageId[node.pid];
    if (parent) parent.children.push(node);
  });

  const decorate = (node, depth) => {
    node.depth = depth;
    node.section = depth === 0 ? null : node.slug.split('/')[0];
    node.children.sort(compareSiblings);
    node.templates = templatesFor(node, templatesBySection);
    node.children.forEach((child) => decorate(child, depth + 1));
  };

  if (home) decorate(home, 0);

  return { home, bySlug, byPageId, nodes };
}

export function findTreeNode(tree, slug) {
  if (!tree) return null;
  const key = recordSlug({ slug });
  return tree.bySlug[key] || (key === HOME_SLUG ? tree.home : null);
}

/**
 * True when `node` sits under `ancestorPageId` by walking pid.
 */
export function isDescendantOf(tree, ancestorPageId, node) {
  if (!tree || !ancestorPageId || !node) return false;
  const seen = {};
  let pid = node.pid;
  while (pid && !seen[pid]) {
    if (pid === ancestorPageId) return true;
    seen[pid] = true;
    const parent = tree.byPageId[pid];
    if (!parent) break;
    pid = parent.pid;
  }
  return false;
}

/**
 * Drop `slug` onto `parentSlug` in the team tree. Home cannot move.
 * A page cannot become its own parent or move under a descendant.
 */
export function canReparent(tree, slug, parentSlug) {
  if (!tree || !slug || !parentSlug) return false;
  if (slug === HOME_SLUG) return false;
  const node = findTreeNode(tree, slug);
  const parent = findTreeNode(tree, parentSlug);
  if (!node?.pageId || !parent?.pageId) return false;
  if (node.pageId === parent.pageId) return false;
  return !isDescendantOf(tree, node.pageId, parent);
}

export function ancestorsOf(tree, slug) {
  const start = findTreeNode(tree, slug);
  if (!start) return [];
  const out = [];
  let pid = start.pid;
  while (pid) {
    const parent = tree.byPageId[pid];
    if (!parent) break;
    out.unshift(parent);
    pid = parent.pid;
  }
  return out;
}
